import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import Navbar from '../Navbar/Navbar';
import Footer from '../Footer/Footer';
import FeedbackButton from '../Feedback/FeedbackButton';
import MaintenanceOverlay from '../MaintenanceOverlay/MaintenanceOverlay';
import BottomTabBar from '../BottomTabBar/BottomTabBar';
import CookieConsent from '../CookieConsent/CookieConsent';
import { AdSenseBanner, useAdSenseScript } from '../AdSense/AdSense';
import { Analytics } from '@vercel/analytics/react';
import { subscribeFeedbackSettings, subscribeMaintenanceSettings } from '@3dera/core/services/admin';
import { useAuth } from '../../context/AuthContext';
import { hasRole, updateUserLegalConsent } from '@3dera/core/services/auth';
import styles from './Layout.module.css';

/** Giriş yapmış kullanıcının yasal onayı yoksa bu sayfaları göstermeye izin ver (okuma / giriş) */
const LEGAL_CONSENT_SKIP_PATHS = ['/auth', '/kullanim-kosullari', '/mesafeli-satis-sozlesmesi', '/kvkk-aydinlatma', '/admin'];

const Layout = ({ children }) => {
  const location = useLocation();

  // Scroll to top on route change
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);
  const { currentUser, userData, refreshUserData } = useAuth();
  const isAdminPage = location.pathname === '/admin';
  const isMessagesPage = location.pathname.startsWith('/mesajlar');
  const [feedbackEnabled, setFeedbackEnabled] = useState(true);

  useAdSenseScript();
  const [maintenance, setMaintenance] = useState({ enabled: false, message: '' });
  const [legalConsentSaving, setLegalConsentSaving] = useState(false);
  const [legalConsentError, setLegalConsentError] = useState('');

  const isAdmin = userData?.roles && hasRole(userData.roles, 'admin');
  const showMaintenanceScreen = maintenance.enabled && !isAdmin;

  const needsLegalConsent = userData && (!userData.termsAcceptedAt || !userData.kvkkAcceptedAt);
  const showLegalConsentOverlay = !!currentUser && needsLegalConsent && !LEGAL_CONSENT_SKIP_PATHS.includes(location.pathname);

  const handleAcceptLegalConsent = async () => {
    if (!currentUser) return;
    setLegalConsentError('');
    setLegalConsentSaving(true);
    const now = new Date().toISOString();
    const result = await updateUserLegalConsent(currentUser.uid, { termsAcceptedAt: now, kvkkAcceptedAt: now });
    setLegalConsentSaving(false);
    if (result.success) {
      await refreshUserData();
    } else {
      setLegalConsentError(result.error || 'Onay kaydedilemedi.');
    }
  };

  // Geri bildirim: Firestore gerçek zamanlı dinleme (admin kapatınca anında güncellenir, F5 gerekmez)
  useEffect(() => {
    const unsubscribe = subscribeFeedbackSettings((data) => {
      setFeedbackEnabled(data.enabled);
    });
    return () => unsubscribe();
  }, []);

  // Bakım modu: Firestore aboneliği + aynı pencerede admin toggle'da custom event (anında güncelleme)
  useEffect(() => {
    const unsubscribe = subscribeMaintenanceSettings((data) => {
      setMaintenance({ enabled: data.enabled, message: data.message || '' });
    });
    const handleMaintenanceEvent = (e) => {
      if (e.detail?.settings) {
        setMaintenance({
          enabled: !!e.detail.settings.enabled,
          message: e.detail.settings.message || ''
        });
      }
    };
    window.addEventListener('maintenanceSettingsUpdated', handleMaintenanceEvent);
    return () => {
      unsubscribe();
      window.removeEventListener('maintenanceSettingsUpdated', handleMaintenanceEvent);
    };
  }, []);

  if (showMaintenanceScreen) {
    return (
      <>
        <MaintenanceOverlay message={maintenance.message} />
        <Analytics />
      </>
    );
  }

  return (
    <div className={styles.layout}>
      <Navbar />
      <main className={`${styles.main} ${isAdminPage ? styles.mainAdmin : ''}`}>
        {children}
      </main>
      {!isAdminPage && !isMessagesPage && <AdSenseBanner />}
      {!isAdminPage && !isMessagesPage && <Footer />}
      {!isAdminPage && !isMessagesPage && feedbackEnabled && <FeedbackButton />}
      {!isAdminPage && <BottomTabBar />}
      <CookieConsent />

      {/* Eski / onaysız hesaplar: site genelinde yasal onay katmanı */}
      {showLegalConsentOverlay && (
        <div className={styles.legalConsentOverlay} role="dialog" aria-modal="true" aria-labelledby="legal-consent-title">
          <div className={styles.legalConsentCard}>
            <h2 id="legal-consent-title" className={styles.legalConsentCardTitle}>Yasal Metinlerin Onayı Gerekli</h2>
            <p className={styles.legalConsentCardText}>
              Yasal düzenlemeler gereği Kullanım Koşulları ve KVKK Aydınlatma Metni onayınız gerekiyor.
              Siteyi kullanmaya devam etmek için lütfen metinleri okuyup aşağıdan kabul edin.
            </p>
            <ul className={styles.legalConsentCardLinks}>
              <li><a href="/kullanim-kosullari" target="_blank" rel="noopener noreferrer">Kullanım Koşulları</a></li>
              <li><a href="/kvkk-aydinlatma" target="_blank" rel="noopener noreferrer">KVKK Aydınlatma Metni</a></li>
            </ul>
            {legalConsentError && <p className={styles.legalConsentCardError}>{legalConsentError}</p>}
            <button
              type="button"
              className={styles.legalConsentCardButton}
              onClick={handleAcceptLegalConsent}
              disabled={legalConsentSaving}
            >
              {legalConsentSaving ? 'Kaydediliyor...' : 'Okudum, kabul ediyorum'}
            </button>
          </div>
        </div>
      )}

      <Analytics />
    </div>
  );
};

export default Layout; 